import { useNodeViewContext } from '@prosemirror-adapter/react';
import { Button, Tooltip, TooltipTrigger, TooltipContent } from '@brainfish-ai/components';
import { Plus } from '@phosphor-icons/react';
import type { MouseEvent } from 'react';

import { useVariableContentChannels } from '../config';
import { handleBlockControlsKeyDown } from '../keymap';
import { appendContentVariantAt, canAddChannelVariant } from '../utils';

/**
 * Outer wrapper: magenta “VARIABLE CONTENT” container showing all variants.
 */
export const VariableContentNodeView = () => {
  const { contentRef, view, getPos, node } = useNodeViewContext();
  const { channels, selectedChannelId } = useVariableContentChannels();
  const readonly = !view.editable;
  const stripChrome = readonly && Boolean(selectedChannelId);
  const canAddVariant = canAddChannelVariant(channels, node);

  const handleAddVariant = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (readonly || !canAddVariant) {
      return;
    }

    const pos = typeof getPos === 'function' ? getPos() : undefined;
    if (typeof pos === 'number') {
      appendContentVariantAt(view, pos, channels);
    }
  };

  if (stripChrome) {
    return <div ref={contentRef} />;
  }

  return (
    <div className="my-3 rounded-lg border-l-4! p-3 bg-magenta-100 border border-magenta-400" data-variable-content>
      <div
        className="px-1 pb-1 text-xs uppercase tracking-wide text-magenta-900 flex items-center justify-between"
        contentEditable={false}
        data-variable-content-controls
        data-variable-content-label
        onKeyDown={(event) => handleBlockControlsKeyDown(event, view)}
        role="toolbar"
        aria-label="Variable content actions"
      >
        <span className="font-semibold">Variable Content</span>
        {!readonly && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label="Add channel variant"
                disabled={!canAddVariant}
                onClick={handleAddVariant}
              >
                <Plus />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Add channel variant</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <div className="flex flex-col gap-3 [&>div]:space-y-1" ref={contentRef} />
    </div>
  );
};
